import { Edit, Insights, Refresh } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../lib/userUtils';
import { getConfigurations } from '../../../modules/config';
import {
  fetchAllExecutionByIteration,
  getTestCaseExecutionsInTestCycle,
} from '../../../modules/executions';
import { getAssociatedUsers } from '../../../modules/project';
import CommonDropdown from '../../elements/Dropdown';
import TestExecutionAccordionItem from './TestExecutionAccordionItem';
import RunAutomationModal from './automation/RunAutomationModal';
import CreateIterationModal from './iterations/CreateIterationModal';
import { IconButton } from '@mui/material';
import IterationInsights from './iterations/IterationInsights';

const PAGE_SIZE = 30;

const FILTER_OPTIONS = [
  { label: 'All executions', value: 'all' },
  { label: 'Assigned to me', value: 'assigned' },
  { label: 'Not executed TCs', value: 'not-executed' },
  { label: 'Archived TC executions', value: 'archived' },
];

const TestExecutionSection = ({
  activeTab,
  testCycleData,
  hide = false,
  viewMode,
  noOfIterations,
  getIterationsByTestCycleId,
  currentIteration,
  setActiveTab,
}) => {
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [executions, setExecutions] = useState([]);
  const [openAccordionKey, setOpenAccordionKey] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [labels, setLabels] = useState([]);
  const [iterationModelMode, setIterationModelMode] = useState('create');
  const [showInsightsModal, setShowInsightsModal] = useState(false)
  const didInitRef = React.useRef(false);

  const defaultStatus =
    Array.isArray(statuses) && statuses.length > 0
      ? statuses.find((s) => s.is_default)
      : undefined;

  const hasMoreExecutions = useMemo(() => {
    if (!Array.isArray(executions)) return false;
    if (executions.length === 0) return false;
    return executions.length < totalCount;
  }, [executions, totalCount]);

  const fetchExecutionData = useCallback(
    async (currentPage = 1, replace = false, filter) => {
      if (!activeTab) return;
      setLoading(true);
      try {
        let response = null;
        if (viewMode === 'iterations') {
          response = await fetchAllExecutionByIteration(
            activeTab,
            currentPage,
            PAGE_SIZE,
            filter // <--- apply filter here
          );
        } else if (viewMode === 'testcases') {
          response = await getTestCaseExecutionsInTestCycle(
            activeTab,
            currentPage,
            PAGE_SIZE
          );
        }

        if (response?.data) {
          setExecutions((prev) => {
            if (replace || currentPage === 1) return response.data;
            const existingIds = new Set(prev.map((p) => p.id));
            const uniqueNew = response.data.filter((d) => !existingIds.has(d.id));
            return prev.concat(uniqueNew);
          });

          setTotalCount(response.total || 0);
        }
      } catch (err) {
        addNotification('danger', 'Failed to fetch execution details');
      } finally {
        setLoading(false);
      }
    },
    [activeTab, viewMode]
  );

  const fetchAllExecutionsForEdit = useCallback(async () => {
    if (!activeTab) return [];
    try {
      setLoading(true);
      let response = null;
      if (viewMode === 'iterations') {
        response = await fetchAllExecutionByIteration(
          activeTab,
          1,
          'all',
          'all'
        );
      }
      return response?.data || [];
    } catch (err) {
      addNotification('danger', 'Failed to fetch execution details');
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeTab, viewMode, selectedFilter]);


  const handleShowMore = useCallback(() => {
    if (loading || !hasMoreExecutions) return;
    setPage((p) => p + 1);
  }, [hasMoreExecutions]);

  const fetchStatuses = useCallback(async () => {
    try {
      const resp = await getConfigurations('testexecutionstatus', projectId);
      if (resp) setStatuses(resp.data || []);
    } catch (err) {
      console.error('fetchStatuses error', err);
    }
  }, [projectId]);

  const getProjectUsers = useCallback(async () => {
    try {
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setUsers((usersResponse || []).map((u) => u?.user).filter(Boolean));
      }
    } catch (err) {
      addNotification('danger', 'Error fetching Jira users:');
    }
  }, [projectId]);

  const fetchLabels = useCallback(async () => {
    try {
      const resp = await getConfigurations('labels', projectId);
      if (resp?.data) setLabels(resp.data);
    } catch (err) {
      console.error('fetchLabels error', err);
    }
  }, [projectId]);

  const handleAccordionSelect = useCallback(
    (eventKey) => {
      setOpenAccordionKey((prev) => (prev === eventKey ? null : eventKey));
    },
    []
  );

  const refreshData = async (resetPage = true, filter = selectedFilter) => {
    if (resetPage) {
      setPage(1);
      await fetchExecutionData(1, true, filter);
    }
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchStatuses();
    getProjectUsers();
    fetchLabels();
  }, []);

  useEffect(() => {
    if (!activeTab) return;
    setPage(1);
    setTotalCount(0);
    setExecutions([]);
    fetchExecutionData(1, true, selectedFilter);
  }, [selectedFilter, activeTab]);

  useEffect(() => {
    if (!activeTab || page === 1) return;
    fetchExecutionData(page, false, selectedFilter);
  }, [page]);


  const handleEditIteration = () => {
    setShowModal(true);
    setIterationModelMode('edit');
  }

  return (
    <>
      <div className="execution-section">

        <>
          {isExecutiveManager() || hide ? null : (
            <Row className="mb-3">
              <Col className="d-flex justify-content-start align-items-center">
                <div className="ms-auto d-flex align-items-center">
                  <Button
                    size="sm"
                    variant="primary"
                    title={
                      testCycleData?.completion_date === null
                        ? 'Start new iteration'
                        : 'Test cycle is already completed'
                    }
                    disabled={!defaultStatus || testCycleData?.completion_date !== null}
                    className="me-2"
                    onClick={() => setShowModal(true)}
                  >
                    + New Test Iteration
                  </Button>

                  {activeTab &&
                    testCycleData?.type === 'Automation' &&
                    testCycleData?.job &&
                    viewMode === 'iterations' && (
                      <Button
                        size="sm"
                        variant="primary"
                        className="me-2"
                        title={
                          testCycleData?.completion_date === null
                            ? 'Run Automation'
                            : 'Test cycle is already completed'
                        }
                        disabled={testCycleData?.completion_date !== null}
                        onClick={() => setShowConfirmationModal(true)}
                      >
                        Run Automation
                      </Button>
                    )}

                  <Button
                    variant="outline-secondary"
                    className="remove-focus me-2"
                    size="sm"
                    onClick={() => refreshData(true)}
                    title="Click to refresh the data"
                  >
                    <Refresh color={'#6c757d'} size={'16px'} />
                  </Button>
                </div>
              </Col>
            </Row>)}
          <div className="mb-3 d-flex align-items-center justify-content-between">
            {viewMode === 'iterations' && noOfIterations > 0 && (
              <>
                <CommonDropdown
                  value={selectedFilter}
                  elementName="executionFilter"
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  options={FILTER_OPTIONS}
                  elementStyle={{ width: 220 }}
                  placeholder="Select filter"
                  required={false}
                />
                <div className="d-flex align-items-center">
                  <IconButton
                    title="Iteration Insights"
                    onClick={() => setShowInsightsModal(true)}
                    className="outlined-icon-btn">
                    <Insights />
                  </IconButton>

                  {isExecutiveManager() || hide ? null : (
                    <IconButton
                      onClick={handleEditIteration}
                      disabled={!defaultStatus || testCycleData?.completion_date !== null}
                      title={
                        testCycleData?.completion_date === null
                          ? 'Edit iteration'
                          : 'Test cycle is already completed'
                      }
                      className="outlined-icon-btn ms-2">
                      <Edit />
                    </IconButton>
                  )}
                </div>
              </>
            )}
          </div>

        </>
        {
          executions?.length > 0 ? (
            <>
              <Accordion activeKey={openAccordionKey} onSelect={handleAccordionSelect} flush>
                {executions.map((execution) => (
                  <React.Fragment key={execution.id}>
                    <TestExecutionAccordionItem
                      openAccordionKey={openAccordionKey}
                      executionId={execution.id}
                      currentExecution={execution}
                      isTestCycleCompleted={testCycleData?.completion_date !== null}
                      statuses={statuses}
                      defaultStatus={defaultStatus}
                      fetchExecutionData={() => refreshData(false)}
                      testcaseData={execution?.association?.testcase}
                      users={users}
                      labels={labels}
                    />
                  </React.Fragment>
                ))}
              </Accordion>

              {hasMoreExecutions && (
                <div className="d-flex justify-content-center mt-3">
                  <Button variant="outline-primary" onClick={handleShowMore} disabled={loading} className="w-50">
                    {loading ? 'Loading More...' : `Show More (${executions.length}/${totalCount})`}
                  </Button>
                </div>
              )}
            </>
          ) : loading && executions.length === 0 ? (
            <div className="message-body">Finding executions...</div>
          ) : (
            <div className="message-body">
              {activeTab
                ? executions?.length === 0
                  ? 'No test executions found'
                  : null
                : viewMode === 'iterations'
                  ? noOfIterations === 0
                    ? null
                    : 'Select an iteration to view executions' +
                    (testCycleData.type === 'Automation' ? ' or run the automation' : '')
                  : 'Select a test case to view executions'}
            </div>
          )
        }
      </div >
      <CreateIterationModal
        showModal={showModal}
        setShowModal={setShowModal}
        defaultStatus={defaultStatus}
        testCycleId={testCycleData?.id}
        fetchExecutionData={() => refreshData(true)}
        getIterationsByTestCycleId={getIterationsByTestCycleId}
        projectId={projectId}
        noOfIterations={noOfIterations}
        testCycleCountries={testCycleData?.geographies || []}
        mode={iterationModelMode}
        setMode={setIterationModelMode}
        executions={executions}
        currentIterationData={
          iterationModelMode === 'edit'
            ? currentIteration
            : null
        }
        onIterationEdited={setActiveTab}
        fetchAllExecutionsForEdit={fetchAllExecutionsForEdit}
      />
      <RunAutomationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        testCycleId={testCycleData?.id}
        testCycleData={testCycleData}
        activeTab={activeTab}
        executions={executions}
      />
      <IterationInsights
        showModal={showInsightsModal}
        handleCloseModal={() => setShowInsightsModal(false)}
        iterationId={currentIteration?.id}
      />
    </>
  );
};

export default TestExecutionSection;